import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type Mode = 'sandbox' | 'production';

interface EnvironmentConfig {
  BASE_URL: string;
  SWITCH_URL: string;
  RECAPTCHA_TOKEN: string;
  fcm_token: string;
  durian_pay: Record<string, any>;
  mode: Mode;
}

const environmentConfigs: Record<Mode, Omit<EnvironmentConfig, 'mode'>> = {
  sandbox: environment,
  production: environment,
};

@Injectable({
  providedIn: 'root',
})
export class ModeSwitchService {
  private initialMode: Mode = this.getVerifiedMode();

  private modeSubject = new BehaviorSubject<EnvironmentConfig>({
    ...environmentConfigs[this.initialMode],
    mode: this.initialMode,
  });

  mode$: Observable<EnvironmentConfig> = this.modeSubject.asObservable();

  constructor() {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'mode' || event.key === 'mode-sig') {
        const verified = this.getVerifiedMode();
        if (this.modeSubject.value.mode !== verified) {
          this.modeSubject.next({
            ...environmentConfigs[verified],
            mode: verified,
          });
        }
      }
    });
  }

  /**
   * Extracts stable identity claims from the JWT payload so that token refreshes
   * (which only change exp/iat and the JWT signature) do not invalidate mode-sig.
   */
  private getStableTokenKey(token: string): string {
    if (!token) return '';
    try {
      const parts = token.split('.');
      if (parts.length >= 2) {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        const { exp, iat, nbf, jti, ...stablePayload } = payload;
        return JSON.stringify(stablePayload);
      }
    } catch {
      // Fallback if token is not a standard JWT
    }
    return localStorage.getItem('email-user') || 'default';
  }

  /**
   * Generates a tamper-proofing hash signature for storing mode in localStorage
   */
  public signMode(mode: Mode, token: string): string {
    const salt = 'komship_env_mode_sec_';
    const stableKey = this.getStableTokenKey(token);
    const str = `${salt}${mode}:${stableKey}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    return hash.toString(36);
  }

  /**
   * Reads and verifies stored mode from localStorage.
   * If tampered by user in DevTools, resets it to 'sandbox' and returns 'sandbox'.
   */
  public getVerifiedMode(): Mode {
    const raw = localStorage.getItem('mode') as Mode | null;
    const sig = localStorage.getItem('mode-sig');
    const token = localStorage.getItem('auth-token') || '';

    if (!raw || (raw !== 'sandbox' && raw !== 'production') || !sig || !token) {
      return 'sandbox';
    }

    const expectedSig = this.signMode(raw, token);
    if (sig !== expectedSig) {
      // Tampering detected! Force fallback to sandbox
      localStorage.setItem('mode', 'sandbox');
      localStorage.setItem('mode-sig', this.signMode('sandbox', token));
      return 'sandbox';
    }

    return raw;
  }

  public isSandboxMode(): boolean {
    const verified = this.getVerifiedMode();
    if (this.modeSubject.value.mode !== verified) {
      this.modeSubject.next({
        ...environmentConfigs[verified],
        mode: verified,
      });
    }
    return verified === 'sandbox';
  }

  public isSandboxMode$(): Observable<boolean> {
    return this.mode$.pipe(map((config) => config.mode === 'sandbox'));
  }

  switchMode(newMode: Mode): void {
    if (newMode in environmentConfigs) {
      const token = localStorage.getItem('auth-token') || '';
      localStorage.setItem('mode', newMode);
      localStorage.setItem('mode-sig', this.signMode(newMode, token));
      this.modeSubject.next({
        ...environmentConfigs[newMode],
        mode: newMode,
      });
    }
  }

  getCurrentMode(): EnvironmentConfig {
    const verified = this.getVerifiedMode();
    if (this.modeSubject.value.mode !== verified) {
      this.modeSubject.next({
        ...environmentConfigs[verified],
        mode: verified,
      });
    }
    return this.modeSubject.getValue();
  }
}
